/**
 * The contact form's public surface: provider, hook, and the lazy boundary
 * (CWS-196).
 *
 * The modal's implementation lives in ``ContactFormModalDialog.tsx`` inside
 * the ``lead-modals`` chunk, fetched on the visitor's **first open** —
 * CWS-179 measured the form libraries it carries (~234 kB of ``zod`` +
 * ``react-hook-form``) as the entry bundle's LCP lever, paid by every first
 * visit for two dialogs most visitors never open. This module must therefore
 * never statically import the dialog module: the only edge to it is the
 * ``import()`` below, and ``tests-node/leadModalChunkGuard.test.ts`` fails
 * the build if a form library resurfaces in an entry-path chunk.
 *
 * Two wrapper behaviours are load-bearing, and both are pinned by the modal
 * suites:
 *
 * * **The first-open latch.** The provider renders ``null`` until the first
 *   open, and from then on keeps the dialog mounted across close → reopen —
 *   its draft, session counter and confirmation state live in the component
 *   instance (``SYSTEM_DESIGN.md`` §15.9, L-g/L-j/L-q/L-r). Conditional
 *   ``{open && …}`` mounting would destroy them on every close.
 * * **The failure boundary.** A first open whose chunk fetch fails is
 *   contained by ``LeadModalBoundary`` (fallback ``null``, one console
 *   trace, one session-latched recovery reload) instead of unmounting the
 *   app. See ``LeadModalBoundary.tsx`` for why a reload is the only honest
 *   recovery and how the latch bounds it.
 */
import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { LeadModalBoundary } from "@/components/LeadModalBoundary";

const ContactFormModalDialog = lazy(() => import("@/components/ContactFormModalDialog"));

export interface ContactFormMeta {
  page: string;
  button: string;
  buttonPosition?: string;
}

// --- Context for opening the modal from anywhere ---

interface ContactFormContextValue {
  openContactForm: (metaOrEvent?: ContactFormMeta | unknown) => void;
}

/**
 * The contact modal's open-action context
 *
 * Exported so a test can inject a spied ``openContactForm`` and pin the
 * behaviour of a call site (the CWS-5 fallback's Contact Us button) without
 * mounting the lazy dialog the real provider would fetch.
 */
export const ContactFormContext = createContext<ContactFormContextValue | null>(null);

const fallback: ContactFormContextValue = { openContactForm: () => {} };

export function useContactForm() {
  const ctx = useContext(ContactFormContext);
  // The no-op fallback stays — it is what lets a call site pass the handler
  // straight to onClick — but it is no longer silent. One provider serves every
  // call site in the app, so a routing or layout change that lifts a subtree out
  // of it would kill the site's primary conversion path with no signal at all
  // (SYSTEM_DESIGN.md §15.9, gap L-k). Development only: the warning is for
  // whoever moved the subtree, not for the visitor.
  if (!ctx && import.meta.env.DEV) {
    console.warn(
      "useContactForm() was called with no ContactFormProvider above it. " +
        "openContactForm() will do nothing and this call site cannot open the contact form.",
    );
  }
  return ctx ?? fallback;
}

const DEFAULT_META: ContactFormMeta = { page: "Unknown", button: "Unknown" };

function isContactFormMeta(v: unknown): v is ContactFormMeta {
  return typeof v === "object" && v !== null && "page" in v && "button" in v;
}

export function ContactFormProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState<ContactFormMeta>(DEFAULT_META);
  // The lazy child mounts on the first open and stays mounted; it is not
  // rendered before that so the chunk is fetched only when a visitor acts.
  const [everOpened, setEverOpened] = useState(false);
  const openContactForm = useCallback((metaOrEvent?: ContactFormMeta | unknown) => {
    const m = isContactFormMeta(metaOrEvent) ? metaOrEvent : DEFAULT_META;
    setMeta(m);
    setOpen(true);
    setEverOpened(true);
  }, []);

  return (
    <ContactFormContext.Provider value={{ openContactForm }}>
      {children}
      {everOpened && (
        <LeadModalBoundary>
          <Suspense fallback={null}>
            <ContactFormModalDialog open={open} onOpenChange={setOpen} meta={meta} />
          </Suspense>
        </LeadModalBoundary>
      )}
    </ContactFormContext.Provider>
  );
}
